import { useMemo } from 'react';
import { css } from '@emotion/react';
import { VoicemailGreetingType_Enum } from '@weave/schema-gen-ts/dist/schemas/phone/callroute/v1/callroute_service.pb';
import { PhoneTree, RouteType } from '@weave/schema-gen-ts/dist/schemas/phone/tree/v1/phone_tree_service.pb';
import { CallRouteV1 } from '@frontend/api-call-route';
import SchemaPhoneTreeService from '@frontend/api-phone-trees';
import { useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useQuery } from '@frontend/react-query-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  Button,
  DropdownField,
  Modal,
  SkeletonLoader,
  Text,
  Truncated,
  useFormField,
  useModalControl,
} from '@frontend/design-system';
import { queryKeys } from '../../../../../query-keys';
import { buildAudioLibraryPath } from '../../../../../utils/media-path';
import { getNameWithoutExtension } from '../../../../../utils/phone-utils';
import { AudioQueryMedia, useAudioQuery } from '../../../../../utils/use-audio-query';
import { usePhoneTreeData } from '../../../../../views/settings/use-phone-tree-data';
import { findMediaItem } from '../../../../audio-picker/audio-picker';
import { CachedAudioScrubber } from '../../../../common/cached-audio-scrubber';
import { DialOptions } from '../../../../phone-tree-quick-actions/phone-tree-card';
import {
  DialOptionData,
  DialOptionInstruction,
  PlayInstruction,
  VoicemailInstruction,
  VoicemailPromptPayload,
} from '../../../../phone-tree-quick-actions/types';
import { getDialOptionChildren, CanvasPhoneTree } from '../../../helpers/graph/node-utils';

// Reusable dropdown component for selecting phone trees
export interface PhoneTreeDropdownProps {
  dropdownProps: ReturnType<typeof useFormField<'dropdown'>>;
  phoneTreesInCallRoute?: CanvasPhoneTree[];
  currentCallRouteId?: string;
  prioritizedLocationId?: string;
  name?: string;
  label?: string;
  placeholder?: string;
  lastUsedPhoneTreeId?: string;
  locationPhoneTenantId?: string;
  shouldFilterOutNotInCallRoute?: boolean; // whether to filter out phone trees that are not in a call route
  onSelect?: (selectedPhoneTree: CanvasPhoneTree | (PhoneTree & { onCanvas: false }) | undefined) => void;
}

export const PhoneTreeDropdown = ({
  dropdownProps,
  phoneTreesInCallRoute,
  currentCallRouteId,
  prioritizedLocationId,
  locationPhoneTenantId,
  name = 'phoneTreeToDuplicate',
  label,
  placeholder,
  lastUsedPhoneTreeId,
  shouldFilterOutNotInCallRoute,
  onSelect,
  ...rest
}: PhoneTreeDropdownProps) => {
  const { t } = useTranslation('phone');
  const { getScopeName, isSingleTypeScope } = useAppScopeStore();
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');

  const notInCallRouteText = t('Not in Call Routes');
  const mostRecentlyUsedText = t('Most Recently Used');

  const phoneTenantId = locationPhoneTenantId || settingsTenantLocation?.phoneTenantId;

  // Fetch saved phone trees
  const { data: savedPhoneTrees = [] } = useQuery({
    queryKey: queryKeys.phoneTrees(phoneTenantId),
    queryFn: () =>
      SchemaPhoneTreeService.List(
        { tenantId: phoneTenantId || '' },
        { params: { 'includes.count_subsequent_phone_trees': true } }
      ),
    select: (data) => {
      const phoneData = data?.phoneTrees;
      if (!phoneData) {
        return [];
      }

      return phoneData.sort((a: PhoneTree, b: PhoneTree) => (!a.name || !b.name ? 0 : a.name.localeCompare(b.name)));
    },
    enabled: !!phoneTenantId,
  });

  // Fetch audio files
  const { data: audioFiles } = useAudioQuery({
    tenantId: phoneTenantId ?? '',
    allowedOptions: { custom: true, standard: true, mailbox: false },
  });

  // Find the last used phone tree from either source
  const lastUsedPhoneTree = useMemo(() => {
    if (!lastUsedPhoneTreeId) return null;

    // Check in phoneTreesInCallRoute first
    const fromCallRoute = phoneTreesInCallRoute?.find((tree) => tree.id === lastUsedPhoneTreeId);
    if (fromCallRoute) {
      return { tree: fromCallRoute, source: 'callRoute' as const };
    }

    // Check in savedPhoneTrees
    const fromSaved = savedPhoneTrees.find((tree) => tree.id === lastUsedPhoneTreeId);
    if (fromSaved) {
      return { tree: { ...fromSaved, onCanvas: false as const }, source: 'saved' as const };
    }

    return null;
  }, [lastUsedPhoneTreeId, phoneTreesInCallRoute, savedPhoneTrees]);

  // Filter out the last used phone tree from phoneTreesInCallRoute
  const filteredPhoneTreesInCallRoute = useMemo(() => {
    if (!lastUsedPhoneTree || lastUsedPhoneTree.source !== 'callRoute') {
      return phoneTreesInCallRoute;
    }
    return phoneTreesInCallRoute?.filter((tree) => tree.id !== lastUsedPhoneTreeId);
  }, [phoneTreesInCallRoute, lastUsedPhoneTree, lastUsedPhoneTreeId]);

  const phoneTreesByLocationId = savedPhoneTrees.reduce<
    Record<string, { locationName: string; locationId: string; trees: (PhoneTree & { onCanvas: false })[] }>
  >((acc, tree) => {
    if (tree.callRouteData?.id === currentCallRouteId) {
      // Already on canvas, skip (canvasPhoneTrees is the source of truth for this category)
      return acc;
    }

    // Skip the last used phone tree if it came from saved trees (it will be shown in its own group)
    if (lastUsedPhoneTree?.source === 'saved' && tree.id === lastUsedPhoneTreeId) {
      return acc;
    }

    const locId = tree.callRouteData?.locationId || 'unknown'; // 'unknown' for global trees so they get grouped together
    if (!acc[locId]) {
      acc[locId] = {
        locationId: locId,
        locationName: getScopeName(locId) || notInCallRouteText,
        trees: [{ ...tree, onCanvas: false }],
      };

      return acc;
    }

    acc[locId].trees.push({ ...tree, onCanvas: false });
    return acc;
  }, {});

  const allPhoneTrees = useMemo(() => {
    const allSavedPhoneTrees = Object.values(phoneTreesByLocationId).flatMap((group) => group.trees);
    const combinedTrees = [...(phoneTreesInCallRoute ?? []), ...allSavedPhoneTrees];
    // Include the last used phone tree in allPhoneTrees for selection lookup
    if (lastUsedPhoneTree) {
      return [lastUsedPhoneTree.tree, ...combinedTrees];
    }
    return combinedTrees;
  }, [phoneTreesInCallRoute, phoneTreesByLocationId, lastUsedPhoneTree]);

  const sortedPhoneTreesByLocation = useMemo(
    () =>
      Object.values(phoneTreesByLocationId)
        .filter(({ locationName }) => !shouldFilterOutNotInCallRoute || locationName !== notInCallRouteText)
        .sort((a, b) => {
          // Sort the groups by location name, but current location should come first
          if (a.locationId === prioritizedLocationId) return -1;
          if (b.locationId === prioritizedLocationId) return 1;
          return a.locationName.localeCompare(b.locationName);
        }),
    [phoneTreesByLocationId, notInCallRouteText, prioritizedLocationId]
  );

  // Call onSelect when the dropdown value changes
  const handleChange = (e: { name: string; value: string }) => {
    dropdownProps.onChange(e);
    if (onSelect) {
      const selectedPhoneTree = allPhoneTrees.find((tree) => tree.id === e.value);
      onSelect(selectedPhoneTree);
    }
  };

  return (
    <DropdownField
      {...dropdownProps}
      onChange={handleChange}
      name={name}
      label={label ?? t('Select Phone Tree')}
      placeholder={placeholder ?? t('Choose a phone tree...')}
      dropdownListStyle={css`
        z-index: ${theme.zIndex
          .modals} !important; // lower the z-index so that the dropdown appears below the "view phone tree" modal
      `}
      {...rest}
    >
      {/* Most Recently Used Phone Tree */}
      {lastUsedPhoneTree && (
        <DropdownField.OptionGroup label={mostRecentlyUsedText}>
          <DropdownField.Option
            key={lastUsedPhoneTree.tree.id}
            value={lastUsedPhoneTree.tree.id}
            displayValue={lastUsedPhoneTree.tree.name}
            css={css`
              padding-left: ${theme.spacing(1)} !important;
            `}
          >
            {lastUsedPhoneTree.source === 'callRoute' ? (
              <CanvasPhoneTreeOption
                phoneTree={lastUsedPhoneTree.tree as CanvasPhoneTree}
                audioFiles={audioFiles ?? { customMedia: [], standardMedia: [], mailboxGreetings: [], allMedia: [] }}
              />
            ) : (
              <PhoneTreeOption
                phoneTree={lastUsedPhoneTree.tree}
                audioFiles={audioFiles ?? { customMedia: [], standardMedia: [], mailboxGreetings: [], allMedia: [] }}
              />
            )}
          </DropdownField.Option>
        </DropdownField.OptionGroup>
      )}

      {/* Phone Trees in this Call Route */}
      {filteredPhoneTreesInCallRoute && filteredPhoneTreesInCallRoute.length > 0 && (
        <DropdownField.OptionGroup label={t('Phone Trees in this Call Route')}>
          {filteredPhoneTreesInCallRoute.map((phoneTree) => {
            return (
              <DropdownField.Option
                key={phoneTree.id}
                value={phoneTree.id}
                displayValue={phoneTree.name}
                css={css`
                  padding-left: ${theme.spacing(1)} !important;
                `}
              >
                <CanvasPhoneTreeOption
                  phoneTree={phoneTree}
                  audioFiles={audioFiles ?? { customMedia: [], standardMedia: [], mailboxGreetings: [], allMedia: [] }}
                />
              </DropdownField.Option>
            );
          })}
        </DropdownField.OptionGroup>
      )}

      {/* Saved Phone Trees grouped by Location */}
      {sortedPhoneTreesByLocation.map(({ locationName, locationId, trees }) => (
        <DropdownField.OptionGroup
          key={locationId}
          label={
            locationId === prioritizedLocationId
              ? isSingleTypeScope
                ? `${
                    !lastUsedPhoneTree && filteredPhoneTreesInCallRoute && filteredPhoneTreesInCallRoute.length === 0
                      ? // For single locations, If there are no phone trees in the call route and no most recently used,
                        // we don't want to show "Other Phone Trees"
                        // NOTE: Not sure why, but we need at least a space here since when I used an empty string,
                        // then selecting an option, would cause the field to only show the id and not the
                        // displayValue of that option ¯\_(ツ)_/¯
                        ' '
                      : t('Other Phone Trees')
                  }`
                : `${locationName} (${t('This Location')})`
              : locationName
          }
        >
          {trees.map((phoneTree) => (
            <DropdownField.Option
              key={phoneTree.id}
              value={phoneTree.id}
              displayValue={phoneTree.name}
              css={css`
                padding-left: ${theme.spacing(1)} !important;
              `}
            >
              <PhoneTreeOption
                phoneTree={phoneTree}
                audioFiles={audioFiles ?? { customMedia: [], standardMedia: [], mailboxGreetings: [], allMedia: [] }}
              />
            </DropdownField.Option>
          ))}
        </DropdownField.OptionGroup>
      ))}
    </DropdownField>
  );
};

// Component used to render a dropdown option for a phone tree that is NOT in the call route canvas currently being viewed.
const PhoneTreeOption = ({ phoneTree, audioFiles }: { phoneTree: PhoneTree; audioFiles: AudioQueryMedia }) => {
  const { modalProps, triggerProps } = useModalControl();

  return (
    <>
      <PhoneTreeOptionRenderer
        name={phoneTree.name ?? ''}
        subsequentPhoneTreeCount={phoneTree.subsequentPhoneTreeCount ?? 0}
        onViewClick={triggerProps.onClick}
      />

      <Modal {...modalProps} minWidth={800} maxWidth={800}>
        <PhoneTreePreviewModal
          phoneTreeId={phoneTree.id}
          name={phoneTree.name ?? ''}
          onClose={modalProps.onClose}
          audioFiles={audioFiles}
        />
      </Modal>
    </>
  );
};

export const PhoneTreePreviewModal = ({
  phoneTreeId,
  name,
  onClose,
  audioFiles,
  precomputedData,
}: {
  phoneTreeId?: string;
  name: string;
  onClose: () => void;
  audioFiles: AudioQueryMedia;
  precomputedData?: {
    dialOptions: DialOptionData[];
    extensionDialingEnabled?: boolean;
    promptMediaItemId: string;
  };
}) => {
  const { t } = useTranslation('phone');

  // Only fetch data if we don't have precomputed data
  const { phoneTreeData, phoneTreeIsLoading, transformedOptions } = usePhoneTreeData({
    phoneTreeId: phoneTreeId ?? '',
    callObjectsQueryOptions: { staleTime: 1 * 60 * 1000 },
  });

  // Use precomputed data if available, otherwise use fetched data
  const dialOptions = precomputedData?.dialOptions ?? transformedOptions;
  const extensionDialingEnabled = precomputedData?.extensionDialingEnabled ?? phoneTreeData?.extensionDialing;
  const promptMediaItemId = precomputedData?.promptMediaItemId ?? phoneTreeData?.promptMediaItemId;
  const isLoading = !precomputedData && phoneTreeIsLoading;
  const hasData = precomputedData || phoneTreeData;

  return (
    <>
      <Modal.Header onClose={onClose}>
        {t('Preview {{phoneTreeName}}', { phoneTreeName: name })}
        <Text weight='regular'>{t('View dial numbers and see where calls go when someone selects that number.')}</Text>
      </Modal.Header>
      <Modal.Body
        css={css`
          max-height: 600px;
          overflow: auto;
        `}
      >
        {isLoading ? (
          <div
            css={css`
              display: flex;
              width: 100%;
              flex-direction: column;
              gap: ${theme.spacing(2)};
            `}
          >
            <SkeletonLoader height={72} />
            <SkeletonLoader height={72} />
            <SkeletonLoader height={72} />
          </div>
        ) : hasData ? (
          <>
            <PhoneTreePromptSection mediaItemId={promptMediaItemId ?? ''} audioFiles={audioFiles} />
            <Text weight='bold'>{t('Dial Options')}</Text>
            <DialOptions options={dialOptions} isReadOnly={true} disableEditing={true} />
          </>
        ) : (
          <Text weight='bold'>{t('No data available.')}</Text>
        )}
      </Modal.Body>
      {hasData && (
        <Modal.Footer>
          <div>
            <Text weight='bold'>
              {extensionDialingEnabled ? t('Dial By Extension Enabled') : t('Dial By Extension Disabled')}
            </Text>
            <Text>
              {extensionDialingEnabled
                ? t('* Callers in this Phone Tree can dial office device extensions. ')
                : t("* Callers in this Phone Tree can't dial office device extensions. ")}
            </Text>
          </div>
        </Modal.Footer>
      )}
    </>
  );
};

const PhoneTreePromptSection = ({ mediaItemId, audioFiles }: { mediaItemId: string; audioFiles: AudioQueryMedia }) => {
  const { t } = useTranslation('phone');

  if (!mediaItemId) {
    return null;
  }

  let filePath = '';
  const media = findMediaItem(mediaItemId, audioFiles?.allMedia || []);
  if (media) {
    filePath = buildAudioLibraryPath({ media });
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing(0.5)};
        max-width: 240px;
        position: relative;
      `}
    >
      <Text weight='bold'>{t('Prompt')}</Text>
      <Truncated as={Text} size='small' color='subdued'>
        {getNameWithoutExtension(media?.name)}
      </Truncated>
      <CachedAudioScrubber singlePlayer mediaId={mediaItemId} filePath={filePath} />
    </div>
  );
};

// Shared component for rendering subsequent tree count suffix
const PhoneTreeCountSuffix = ({ count }: { count: number }) => {
  const { t } = useTranslation('phone');

  if (count === 0) return null;

  return (
    <Text
      as='span'
      color='subdued'
      css={css`
        padding-left: ${theme.spacing(0.5)};
      `}
    >
      {t(`+ {{count}} subsequent tree`, { count })}
    </Text>
  );
};

// Shared component for rendering phone tree option UI
const PhoneTreeOptionRenderer = ({
  name,
  subsequentPhoneTreeCount,
  onViewClick,
}: {
  name: string;
  subsequentPhoneTreeCount: number;
  onViewClick: (e: React.MouseEvent) => void;
}) => {
  return (
    <div
      css={css`
        display: flex;
        align-items: center;
      `}
    >
      <Button
        variant='tertiary'
        iconName='view'
        onClick={(e) => {
          e.stopPropagation();
          onViewClick(e);
        }}
      />
      <span
        css={css`
          width: 89%;
          display: flex;
        `}
      >
        <Truncated
          css={css`
            width: fit-content;
          `}
        >
          {name}
        </Truncated>
        <PhoneTreeCountSuffix count={subsequentPhoneTreeCount} />
      </span>
    </div>
  );
};

/////////////
// CALL ROUTE SPECIFIC - FOR WHEN PHONE TREE DROPDOWN IS USED IN THE CALL ROUTE CANVAS
/////////////
const mapNodeTypeToRouteType = (nodeType: CallRouteV1.Types.NodeType): RouteType => {
  switch (nodeType) {
    case CallRouteV1.Types.NodeType.NODE_TYPE_CALL_GROUP:
      return RouteType.ROUTE_TYPE_CALL_GROUP;
    case CallRouteV1.Types.NodeType.NODE_TYPE_CALL_QUEUE:
      return RouteType.ROUTE_TYPE_CALL_QUEUE;
    case CallRouteV1.Types.NodeType.NODE_TYPE_CALL_ROUTE:
    case CallRouteV1.Types.NodeType.NODE_TYPE_DEVICE_EXTENSION:
      return RouteType.ROUTE_TYPE_CALL_ROUTE;
    case CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_DEVICE:
      return RouteType.ROUTE_TYPE_DEVICE;
    case CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_NUMBER:
      return RouteType.ROUTE_TYPE_FORWARDING_NUMBER;
    case CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE:
      return RouteType.ROUTE_TYPE_IVR_MENU;
    case CallRouteV1.Types.NodeType.NODE_TYPE_PLAY_MESSAGE:
      return RouteType.ROUTE_TYPE_PLAY_MESSAGE;
    case CallRouteV1.Types.NodeType.NODE_TYPE_REPEAT:
      return RouteType.ROUTE_TYPE_REPEAT;
    case CallRouteV1.Types.NodeType.NODE_TYPE_VOICEMAIL_BOX:
      return RouteType.ROUTE_TYPE_VOICEMAIL;
    case CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS:
      return RouteType.ROUTE_TYPE_PHONE_HOURS;
    case CallRouteV1.Types.NodeType.NODE_TYPE_WEAVE_ASSISTANT:
      return RouteType.ROUTE_TYPE_WEAVE_ASSISTANT;
    default:
      return RouteType.ROUTE_TYPE_UNSPECIFIED;
  }
};

const createPayload = (node: CallRouteV1.Types.Node, audioFiles: AudioQueryMedia): DialOptionInstruction => {
  const { callObject } = node;
  const routeType = mapNodeTypeToRouteType(node.type);

  switch (node.type) {
    case CallRouteV1.Types.NodeType.NODE_TYPE_CALL_GROUP:
      if ('callGroupExpansion' in callObject) {
        return {
          type: routeType,
          id: callObject.primitiveId,
          name: callObject.primitiveName || 'Unknown Call Group',
        } as DialOptionInstruction;
      }
      break;
    case CallRouteV1.Types.NodeType.NODE_TYPE_CALL_QUEUE:
      if ('callQueueExpansion' in callObject) {
        return {
          type: routeType,
          id: callObject.primitiveId,
          name: callObject.primitiveName || 'Unknown Call Queue',
        } as DialOptionInstruction;
      }
      break;
    case CallRouteV1.Types.NodeType.NODE_TYPE_CALL_ROUTE:
    case CallRouteV1.Types.NodeType.NODE_TYPE_DEVICE_EXTENSION:
      if ('callRouteExpansion' in callObject) {
        return {
          type: routeType,
          id: callObject.primitiveId,
          name: callObject.primitiveName || 'Unknown Call Route',
        } as DialOptionInstruction;
      }
      break;
    case CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_DEVICE:
      if ('deviceExpansion' in callObject) {
        return {
          type: routeType,
          id: callObject.primitiveId,
          name: callObject.primitiveName || 'Unknown Device',
        } as DialOptionInstruction;
      }
      break;
    case CallRouteV1.Types.NodeType.NODE_TYPE_FORWARD_NUMBER:
      if ('forwardingNumberExpansion' in callObject) {
        return {
          type: routeType,
          id: callObject.primitiveId,
          name: callObject.primitiveName || 'Unknown Forwarding Number',
        } as DialOptionInstruction;
      }
      break;
    case CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE:
      if ('phoneTreeExpansion' in callObject) {
        return {
          type: routeType,
          id: callObject.primitiveId,
          name: callObject.primitiveName || 'Unknown Phone Tree',
        } as DialOptionInstruction;
      }
      break;
    case CallRouteV1.Types.NodeType.NODE_TYPE_PLAY_MESSAGE:
      if ('playMessageExpansion' in callObject) {
        const mediaItemId = callObject.playMessageExpansion.mediaItemId;
        let filePath = '';
        const media = findMediaItem(mediaItemId, audioFiles?.allMedia || []);
        if (media) {
          filePath = buildAudioLibraryPath({ media });
        }
        return {
          type: routeType,
          id: mediaItemId,
          name: callObject.primitiveName || 'Unknown Play Message',
          data: {
            filePath,
          },
        } as PlayInstruction;
      }
      break;
    case CallRouteV1.Types.NodeType.NODE_TYPE_VOICEMAIL_BOX:
      if ('voicemailExpansion' in callObject) {
        const data: VoicemailPromptPayload = {
          voicemailBoxId: callObject.primitiveId,
          // this is the default greeting case: skipGreeting is false and none of the other fields are set.
          skipGreeting: false,
          systemMediaId: '',
          voicemailGreetingId: '',
        };

        if (callObject.voicemailExpansion.greetingType === VoicemailGreetingType_Enum.NO_GREETING) {
          data.skipGreeting = true;
        } else if (callObject.voicemailExpansion.greetingType === VoicemailGreetingType_Enum.NUMBERED_GREETING) {
          data.voicemailGreetingId = callObject.voicemailExpansion.greetingMediaItemId;
        } else if (callObject.voicemailExpansion.greetingType === VoicemailGreetingType_Enum.AUDIO_LIBRARY_GREETING) {
          data.systemMediaId = callObject.voicemailExpansion.greetingMediaItemId;
        }
        return {
          type: routeType,
          id: callObject.primitiveId,
          name: callObject.primitiveName || 'Unknown Voicemail Box',
          data,
        } as VoicemailInstruction;
      }
      break;
    case CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS:
      if ('phoneHoursExpansion' in callObject) {
        return {
          type: routeType,
          id: callObject.primitiveId,
          name: callObject.primitiveName || 'Unknown Phone Hours',
        } as DialOptionInstruction;
      }
      break;
  }

  // Fallback for unhandled types
  return {
    type: routeType,
    id: callObject.primitiveId || '',
    name: callObject.primitiveName || 'Unknown',
  } as DialOptionInstruction;
};

// This function traverses the phone tree graph and converts it into an array of DialOptionData.
const transormGraphToDialOptions = ({
  nodes,
  edges,
  phoneTreeNodeId,
  audioFiles,
}: {
  nodes: CanvasPhoneTree['nodes'];
  edges: CanvasPhoneTree['edges'];
  phoneTreeNodeId: string;
  audioFiles: AudioQueryMedia;
}): DialOptionData[] => {
  const dialOptionNodes = getDialOptionChildren(phoneTreeNodeId, nodes, edges);
  return dialOptionNodes
    .map((optionNode) => {
      const number = parseInt(optionNode.callObject.primitiveName, 10);
      const routes: DialOptionInstruction[] = [];

      let currentNode: CallRouteV1.Types.Node | undefined = optionNode;

      // Traverse the graph to find the sequence of routes
      // The first step is the child of the TreeOptionNode
      const firstEdge = edges.find((e) => e.sourceId === optionNode.id);
      if (firstEdge) {
        currentNode = nodes.find((n) => n.id === firstEdge.targetId);
      } else {
        currentNode = undefined;
      }

      while (currentNode) {
        const nodeId = currentNode.id;
        // Skip Fallback nodes, but follow their edges
        if (currentNode.type === CallRouteV1.Types.NodeType.NODE_TYPE_FALLBACK) {
          const nextEdge = edges.find((e) => e.sourceId === nodeId);
          currentNode = nextEdge ? nodes.find((n) => n.id === nextEdge.targetId) : undefined;
          continue;
        }

        const routeType = mapNodeTypeToRouteType(currentNode.type);
        if (routeType !== RouteType.ROUTE_TYPE_UNSPECIFIED) {
          routes.push(createPayload(currentNode, audioFiles));
        }

        if (
          currentNode.type === CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_TREE ||
          currentNode.type === CallRouteV1.Types.NodeType.NODE_TYPE_PHONE_HOURS
        ) {
          // Stop traversal at Phone Tree nodes to avoid going into nested trees.
          break;
        }

        // Check for fallback/next step
        const nextEdge = edges.find((e) => e.sourceId === nodeId);
        if (nextEdge) {
          currentNode = nodes.find((n) => n.id === nextEdge.targetId);
        } else {
          currentNode = undefined;
        }
      }

      return {
        number,
        readOnly: true,
        routes,
      };
    })
    .sort((a, b) => a.number - b.number);
};

// Component used to render a dropdown option for a phone tree that is in the call route canvas currently being viewed.
const CanvasPhoneTreeOption = ({
  phoneTree,
  audioFiles,
}: {
  phoneTree: CanvasPhoneTree;
  audioFiles: AudioQueryMedia;
}) => {
  const dialOptions = useMemo(
    () =>
      transormGraphToDialOptions({
        nodes: phoneTree.nodes,
        edges: phoneTree.edges,
        phoneTreeNodeId: phoneTree.phoneTreeNodeId,
        audioFiles,
      }),
    [phoneTree.nodes, phoneTree.edges, phoneTree.phoneTreeNodeId, audioFiles]
  );

  const { modalProps, triggerProps } = useModalControl();

  return (
    <>
      <PhoneTreeOptionRenderer
        name={phoneTree.name}
        subsequentPhoneTreeCount={phoneTree.subsequentPhoneTreeCount ?? 0}
        onViewClick={triggerProps.onClick}
      />

      <Modal {...modalProps} minWidth={800} maxWidth={800}>
        <PhoneTreePreviewModal
          name={phoneTree.name}
          onClose={modalProps.onClose}
          audioFiles={audioFiles}
          precomputedData={{
            dialOptions,
            extensionDialingEnabled: phoneTree.extensionDialingEnabled,
            promptMediaItemId: phoneTree.promptMediaItemId,
          }}
        />
      </Modal>
    </>
  );
};
